import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useUserInfoSelector } from "store/reducers";
import {
  fetchMarketplaceTherapists,
  fetchMarketplaceTherapistsAvailability,
} from "actions/marketplace";
import MarketplaceFilters from "./components/MarketplaceFilters";
import PractitionerCard from "./components/PractitionerCard";
import BlissAssistantCard from "./components/BlissAssistantCard";
import FeatureGate from "components/FeatureGate";
import WaveIcon from "assets/svg_icons/wave_icon.svg";
import "./index.css";

const AVAILABILITY_BATCH_DELAY_MS = 120;
const createListingSeed = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const DEFAULT_FILTERS = {
  expertise: [],
  languages: [],
  experience: [],
  rating: [],
  featured: [],
  pricingRange: null,
};

const EXPERIENCE_RANGES = [
  { label: "1-2 years", min: 1, max: 2 },
  { label: "2-5 years", min: 2, max: 5 },
  { label: "5-7 years", min: 5, max: 7 },
  { label: "7+ years", min: 7, max: null },
];

const buildApiParams = ({ search, filters, page }) => {
  const selectedExperienceRanges = EXPERIENCE_RANGES.filter((range) =>
    filters.experience.includes(range.label)
  );
  const minExperience =
    selectedExperienceRanges.length > 0
      ? Math.min(...selectedExperienceRanges.map((range) => range.min))
      : undefined;
  const finiteMaxExperience = selectedExperienceRanges
    .map((range) => range.max)
    .filter((value) => value !== null);
  const maxExperience =
    selectedExperienceRanges.length > 0 &&
    finiteMaxExperience.length === selectedExperienceRanges.length
      ? Math.max(...finiteMaxExperience)
      : undefined;
  const selectedRatings = filters.rating.map(Number).filter(Number.isFinite);
  const minRating = selectedRatings.length > 0 ? Math.min(...selectedRatings) : undefined;
  const minPrice = Number(filters.pricingRange?.min);
  const maxPrice = Number(filters.pricingRange?.max);

  return {
    page,
    limit: 50,
    userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    search: search || undefined,
    expertise: filters.expertise.join(",") || undefined,
    languages: filters.languages.join(",") || undefined,
    minExperience,
    maxExperience,
    minRating,
    topRated: filters.featured.includes("Top Rated") ? true : undefined,
    mostBooked: filters.featured.includes("Most Booked") ? true : undefined,
    minPrice: Number.isFinite(minPrice) ? minPrice : undefined,
    maxPrice: Number.isFinite(maxPrice) ? maxPrice : undefined,
  };
};

const enrichTherapistsWithLoadingAvailability = (therapists = []) =>
  therapists.map((therapist) => ({
    ...therapist,
    availability: therapist.availability || null,
    availabilityLoading: true,
  }));

const Marketplace = ({ userTherapistId="" }) => {
  const userInfo = useUserInfoSelector();
  const history = useHistory();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [therapists, setTherapists] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    expertise: [],
    languages: [],
    pricing: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const latestRequestRef = useRef(0);
  const listingSeedRef = useRef(createListingSeed());
  const availabilityObserverRef = useRef(null);
  const availabilityQueueRef = useRef(new Set());
  const requestedAvailabilityIdsRef = useRef(new Set());
  const availabilityBatchTimerRef = useRef(null);
  const availabilityRequestCycleRef = useRef(0);

  const clearAvailabilityBatchTimer = () => {
    if (availabilityBatchTimerRef.current) {
      clearTimeout(availabilityBatchTimerRef.current);
      availabilityBatchTimerRef.current = null;
    }
  };

  const resetAvailabilityLoadingState = () => {
    clearAvailabilityBatchTimer();
    availabilityQueueRef.current = new Set();
    requestedAvailabilityIdsRef.current = new Set();
    availabilityRequestCycleRef.current += 1;
  };

  const loadAvailabilityBatch = async (therapistIds, requestCycle) => {
    if (!therapistIds.length) {
      return;
    }

    try {
      const response = await fetchMarketplaceTherapistsAvailability({
        therapistIds: therapistIds.join(","),
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (requestCycle !== availabilityRequestCycleRef.current) {
        return;
      }

      const availabilityByTherapistId = response.data?.availabilityByTherapistId || {};

      setTherapists((currentTherapists) => {
        const therapistIdSet = new Set(therapistIds);
        return currentTherapists.map((therapist) =>
          therapistIdSet.has(therapist._id)
            ? {
                ...therapist,
                availability: availabilityByTherapistId[therapist._id] || therapist.availability,
                availabilityLoading: false,
              }
            : therapist
        );
      });
    } catch (availabilityError) {
      if (requestCycle !== availabilityRequestCycleRef.current) {
        return;
      }

      setTherapists((currentTherapists) => {
        const therapistIdSet = new Set(therapistIds);
        return currentTherapists.map((therapist) =>
          therapistIdSet.has(therapist._id)
            ? { ...therapist, availabilityLoading: false }
            : therapist
        );
      });
    }
  };

  const flushAvailabilityQueue = () => {
    const therapistIds = [...availabilityQueueRef.current].filter(
      (therapistId) => !requestedAvailabilityIdsRef.current.has(therapistId)
    );

    availabilityQueueRef.current.clear();
    availabilityBatchTimerRef.current = null;

    if (!therapistIds.length) {
      return;
    }

    therapistIds.forEach((therapistId) => {
      requestedAvailabilityIdsRef.current.add(therapistId);
    });

    loadAvailabilityBatch(therapistIds, availabilityRequestCycleRef.current);
  };

  const queueAvailabilityLoad = (therapistId) => {
    if (!therapistId || requestedAvailabilityIdsRef.current.has(therapistId)) {
      return;
    }

    availabilityQueueRef.current.add(therapistId);

    if (!availabilityBatchTimerRef.current) {
      availabilityBatchTimerRef.current = setTimeout(
        flushAvailabilityQueue,
        AVAILABILITY_BATCH_DELAY_MS
      );
    }
  };

  const registerCardRef = (therapistId, node) => {
    if (!therapistId || !availabilityObserverRef.current || !node) {
      return;
    }

    node.dataset.therapistId = therapistId;
    availabilityObserverRef.current.observe(node);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 1500);

    return () => clearTimeout(debounceTimer);
  }, [searchValue]);

  useEffect(() => {
    availabilityObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const therapistId = entry.target.dataset.therapistId;
          queueAvailabilityLoad(therapistId);
          availabilityObserverRef.current?.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: "200px 0px",
        threshold: 0.1,
      }
    );

    return () => {
      clearAvailabilityBatchTimer();
      availabilityObserverRef.current?.disconnect();
      availabilityObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    const loadTherapists = async () => {
      setLoading(true);
      setError("");
      listingSeedRef.current = createListingSeed();
      resetAvailabilityLoadingState();

      try {
        let page = 1;
        let totalPages = 1;
        setTherapists([]);

        do {
          const response = await fetchMarketplaceTherapists(
            {
              ...buildApiParams({ search: debouncedSearch, filters, page }),
              randomSeed: listingSeedRef.current,
            }
          );
          const data = response.data || {};

          if (requestId !== latestRequestRef.current || !isMounted) {
            return;
          }

          const loadedTherapists = enrichTherapistsWithLoadingAvailability((data.therapists).filter(val => val._id !== userTherapistId) || []);
          setTherapists((currentTherapists) =>
            page === 1 ? loadedTherapists : [...currentTherapists, ...loadedTherapists]
          );
          totalPages = data.pagination?.totalPages || 1;

          if (data.filterOptions) {
            setFilterOptions(data.filterOptions);
          }

          page += 1;
        } while (page <= totalPages);
      } catch (apiError) {
        if (isMounted && requestId === latestRequestRef.current) {
          setTherapists([]);
          setError("Unable to load therapists right now.");
        }
      } finally {
        if (isMounted && requestId === latestRequestRef.current) {
          setLoading(false);
        }
      }
    };

    loadTherapists();

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch, filters]);

  const categories = useMemo(
    () => ["All", ...filterOptions.expertise.map((option) => option.label)],
    [filterOptions.expertise]
  );

  const selectedCategory = filters.expertise.length === 1
    ? filterOptions.expertise.find((option) => option.value === filters.expertise[0])?.label || "All"
    : "All";

  const handleSelectCategory = (category) => {
    if (category === "All") {
      setFilters((currentFilters) => ({ ...currentFilters, expertise: [] }));
      return;
    }

    const selectedExpertise = filterOptions.expertise.find(
      (option) => option.label === category
    );

    setFilters((currentFilters) => ({
      ...currentFilters,
      expertise: selectedExpertise ? [selectedExpertise.value] : [],
    }));
  };

  const handleBookNow = (therapist) => {
    history.push("/dashboard/sessionBooking", {
      fromMarketplace: true,
      marketplaceTherapist: therapist,
    });
  };

  return (
    <section className="marketplace-screen">
      <div className="marketplace-header">
        <h1>
          Hey {userInfo?.name || userInfo?.firstName || "there"}
          <img src={WaveIcon} alt="" aria-hidden="true" />
        </h1>
      </div>

      <MarketplaceFilters
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={handleSelectCategory}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filters={filters}
        filterOptions={filterOptions}
        experienceOptions={EXPERIENCE_RANGES}
        onFiltersChange={setFilters}
      />

      <div className="marketplace-card-grid">
        {loading && therapists.length === 0 &&
          Array.from({ length: 6 }).map((_, index) => (
            <div className="marketplace-card-skeleton" key={index} />
          ))}

        {therapists.map((therapist) => (
          <PractitionerCard
            key={therapist._id}
            practitioner={therapist}
            onBookNow={handleBookNow}
            registerCardRef={registerCardRef}
          />
        ))}
      </div>

      {!loading && !error && therapists.length === 0 && (
        <p className="marketplace-empty-state">No therapists match these filters.</p>
      )}

      {!loading && error && <p className="marketplace-empty-state">{error}</p>}

      <FeatureGate featureKey="chatbot">
        <BlissAssistantCard />
      </FeatureGate>
    </section>
  );
};

export default Marketplace;
